/**
 * Session timeout warning modal component (React port of the Angular
 * `SessionTimeoutWarning`).
 *
 * Displays a warning modal when the user session is about to expire:
 * - Shows countdown timer with time remaining
 * - "Stay Logged In" button to extend the session
 * - "Log Out Now" button for immediate logout
 * - Auto-hides when user activity is detected (driven by the store)
 *
 * Markup, Tailwind classes, hover/animation and behaviour are reproduced 1:1
 * from the Angular component.
 */

import { useRef } from 'react';

import { SvgIcon } from '@/components/common/svg-icon/SvgIcon';
import { useSessionTimeout } from '@/hooks/useSessionTimeout';
import { useLocalAuth } from '@/hooks/useLocalAuth';
import { useFocusTrap } from '@/hooks/useFocusTrap';

export function SessionTimeoutWarning() {
  const { showWarning, timeRemaining, extendSession } = useSessionTimeout();
  const { signOut } = useLocalAuth();

  const modalRef = useRef<HTMLDivElement>(null);
  const stayLoggedInRef = useRef<HTMLButtonElement>(null);

  // Computed: Format time remaining as MM:SS
  const minutes = Math.floor(timeRemaining / 60);
  const remainingSeconds = timeRemaining % 60;
  const formattedTime = `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;

  // User clicked "Stay Logged In" - extend the session
  const onStayLoggedIn = (): void => {
    extendSession();
  };

  // User clicked "Log Out Now" - immediately log out
  const onLogOutNow = async (): Promise<void> => {
    await signOut();
  };

  // Accessibility: trap focus within the dialog while it is shown, move initial
  // focus to the primary "Stay Logged In" button, and restore focus on close.
  // Escape extends the session ("Stay Logged In") rather than logging out — a
  // session warning should never silently sign the user out via a keystroke, so
  // the least-surprising Escape behaviour is the safe, non-destructive action.
  useFocusTrap(modalRef, showWarning, {
    onEscape: onStayLoggedIn,
    initialFocusRef: stayLoggedInRef,
  });

  // Session timeout warning modal
  if (!showWarning) return null;

  return (
    // Backdrop overlay
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      {/* Modal card */}
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="session-timeout-title"
        aria-describedby="session-timeout-description"
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6 space-y-4"
      >
        {/* Header with icon */}
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <SvgIcon className="text-amber-600 dark:text-amber-400" name="clock" size="medium" />
            </div>
          </div>

          <div className="flex-1">
            <h3
              id="session-timeout-title"
              className="text-lg font-semibold text-gray-900 dark:text-gray-100"
            >
              Session Expiring Soon
            </h3>
            <p
              id="session-timeout-description"
              className="text-sm text-gray-600 dark:text-gray-400 mt-1"
            >
              Your session will expire due to inactivity. Would you like to stay logged in?
            </p>
          </div>
        </div>

        {/* Countdown timer */}
        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 text-center">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Time remaining</p>
          <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 tabular-nums">
            {formattedTime}
          </p>
        </div>

        {/* Security notice */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
          <p className="text-xs text-blue-800 dark:text-blue-300">
            <strong>Security Notice:</strong> For your protection, inactive sessions are
            automatically logged out after 30 minutes to maintain HIPAA compliance.
          </p>
        </div>

        {/* Action buttons */}
        <div className="flex flex-col-reverse sm:flex-row gap-3">
          {/* Log Out Now button */}
          <button
            onClick={() => void onLogOutNow()}
            className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 flex items-center justify-center gap-2 text-sm font-medium"
            type="button"
          >
            <SvgIcon name="log-out" size="small" />
            Log Out Now
          </button>

          {/* Stay Logged In button (primary) */}
          <button
            ref={stayLoggedInRef}
            onClick={onStayLoggedIn}
            className="flex-1 px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white transition-colors duration-200 flex items-center justify-center gap-2 text-sm font-medium shadow-sm"
            type="button"
          >
            <SvgIcon name="check-circle" size="small" />
            Stay Logged In
          </button>
        </div>
      </div>
    </div>
  );
}

export default SessionTimeoutWarning;
